/**
 * Canvas → GeneratedStack serializer (conversational revision pipeline).
 *
 * The reverse of the Editor's materializeGeneratedStack: turns the live React
 * Flow canvas into the JSON schema the AI providers emit, so a revise request
 * can hand the model the current diagram and ask for the full updated stack.
 *
 * Deliberately lossy in one direction: positions, handles, zIndex, sizing and
 * styling are NOT serialized — the merge (services/aiRevise.ts) keeps those
 * client-side and re-derives handles from final geometry. Node ids, group
 * membership, labels, icons and layout hints DO round-trip, because the merge
 * matches the model's output to canvas nodes by id.
 *
 * Node types the AI cannot author (shapeNode, anything unknown) are skipped
 * here and left untouched by the merge.
 */
import type { Edge, Node } from 'reactflow';
import type {
  GeneratedEdge,
  GeneratedGroup,
  GeneratedNode,
  GeneratedStack,
  GeneratedStickyNote,
  GeneratedTextNode,
  TechNodeData,
  GroupNodeData,
  StickyNoteData,
  TextNodeData,
} from '../types';

/** Node types that participate in the AI round-trip: techNode, groupNode, stickyNote, textNode. */

/**
 * The editor colors generated edges by relationship type (see
 * materializeGeneratedStack). Reverse that mapping so the model sees the
 * type it originally chose instead of inventing a new one.
 */
const STROKE_TO_EDGE_TYPE: Record<string, NonNullable<GeneratedEdge['type']>> = {
  '#3b82f6': 'data',
  '#10b981': 'api',
  '#f59e0b': 'event',
  '#64748b': 'dependency',
  '#ef4444': 'control',
};

function inferEdgeType(edge: Edge): GeneratedEdge['type'] {
  if (edge.animated) return 'event';
  const stroke = typeof edge.style?.stroke === 'string' ? edge.style.stroke.toLowerCase() : undefined;
  return stroke ? STROKE_TO_EDGE_TYPE[stroke] : undefined;
}

function serializeTechNode(node: Node): GeneratedNode {
  const data = (node.data ?? {}) as TechNodeData & {
    category?: GeneratedNode['category'];
    importance?: GeneratedNode['importance'];
    metadata?: GeneratedNode['metadata'];
  };
  // `icon` is the namespaced form; iconSlug is the deprecated bare SimpleIcons
  // slug kept for old saves — re-namespace it so the model gets one format.
  const icon =
    data.icon ??
    (data.iconSlug ? (data.iconSlug.includes(':') ? data.iconSlug : `si:${data.iconSlug}`) : 'si:cube');
  return {
    id: node.id,
    label: data.label ?? '',
    icon,
    description: data.description ?? '',
    ...(data.category ? { category: data.category } : {}),
    ...(data.importance ? { importance: data.importance } : {}),
    // groupId is the container id on the canvas (parentNode), which is also
    // the GeneratedGroup id we emit below — the model can keep it stable.
    ...(typeof node.parentNode === 'string' ? { groupId: node.parentNode } : {}),
    ...(data.layout ? { layout: data.layout } : {}),
    ...(data.metadata ? { metadata: data.metadata } : {}),
  };
}

/**
 * Serialize the canvas to the AI's stack format. Only `techNode`, `groupNode`,
 * `stickyNote` and `textNode` participate; everything else (shapes, unknown
 * custom types) is skipped, and edges are only emitted between serialized tech
 * nodes. Handles are dropped — they are re-derived from geometry after a merge.
 */
export function canvasToGeneratedStack(nodes: Node[], edges: Edge[]): GeneratedStack {
  const techNodes = nodes.filter((n) => n.type === 'techNode');
  const techIds = new Set(techNodes.map((n) => n.id));

  const generatedNodes: GeneratedNode[] = techNodes.map(serializeTechNode);

  const groups: GeneratedGroup[] = nodes
    .filter((n) => n.type === 'groupNode')
    .map((n) => {
      const data = (n.data ?? {}) as GroupNodeData;
      return {
        id: n.id,
        label: data.label ?? n.id,
        nodeIds: techNodes.filter((t) => t.parentNode === n.id).map((t) => t.id),
      };
    })
    .filter((g) => g.nodeIds.length > 0);

  const stickyNotes: GeneratedStickyNote[] = nodes
    .filter((n) => n.type === 'stickyNote')
    .map((n) => {
      const data = (n.data ?? {}) as StickyNoteData;
      return {
        id: n.id,
        label: data.label ?? '',
        ...(data.color ? { color: data.color } : {}),
      };
    });

  const textNodes: GeneratedTextNode[] = nodes
    .filter((n) => n.type === 'textNode')
    .map((n) => {
      const data = (n.data ?? {}) as TextNodeData;
      return {
        id: n.id,
        label: data.label ?? '',
        ...(data.description ? { description: data.description } : {}),
      };
    });

  const generatedEdges: GeneratedEdge[] = edges
    .filter((e) => techIds.has(e.source) && techIds.has(e.target))
    .map((e) => {
      const type = inferEdgeType(e);
      return {
        source: e.source,
        target: e.target,
        ...(typeof e.label === 'string' && e.label ? { label: e.label } : {}),
        ...(type ? { type } : {}),
        ...(e.markerStart ? { bidirectional: true } : {}),
      };
    });

  return {
    nodes: generatedNodes,
    edges: generatedEdges,
    ...(groups.length > 0 ? { groups } : {}),
    ...(stickyNotes.length > 0 ? { stickyNotes } : {}),
    ...(textNodes.length > 0 ? { textNodes } : {}),
  };
}
